import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { MailCheck, Loader2, PlayCircle } from 'lucide-react';

/** Admin → Settings → Maintenance card for the weekly onboarding
 *  follow-ups (members who haven't completed their profile, merchants
 *  with incomplete profiles, merchants with 0 services). Shows an
 *  enable/disable toggle, a "Run now" button, and the last run summary.
 */
export function OnboardingFollowupsCard() {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/onboarding-followups/status');
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read status');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const toggle = async (enabled) => {
    try {
      await api.post('/api/admin/onboarding-followups/toggle', { enabled });
      toast.success(enabled ? 'Follow-ups enabled' : 'Follow-ups paused');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/api/admin/onboarding-followups/run');
      const m = data.member_profile?.sent ?? 0;
      const mp = data.merchant_profile?.sent ?? 0;
      const ms = data.merchant_services?.sent ?? 0;
      toast.success(`Sent ${m + mp + ms} email${(m + mp + ms) === 1 ? '' : 's'} (${m} members, ${mp} merchant profiles, ${ms} services)${data.daily_cap_reached ? ' · daily limit reached, continues tomorrow' : ''}.`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Run failed');
    }
    setRunning(false);
  };

  const last = status?.last_run;
  return (
    <Card className="bg-[#041A3B] border-[#00E5FF]/30" data-testid="onboarding-followups-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <MailCheck className="w-5 h-5 text-[#00E5FF]" />
          Weekly Onboarding Follow-Ups
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[#A0B4CC] text-xs leading-relaxed">
          Sends a nudge email every week to (1) batch-uploaded members who
          haven&apos;t completed their profile, (2) approved merchants missing
          business name / description, and (3) approved merchants with zero
          active services. Each person gets at most one reminder per week,
          sent in daily batches from 9:00 AM Manila time (shares the {status?.today?.cap ?? 200}/day reminder limit with the merchant digest);
          each day continues with those not yet reminded.
        </p>

        <div className="flex items-center justify-between rounded-md bg-[#000B18]/60 border border-white/5 p-3">
          <div>
            <p className="text-white text-sm font-medium">Automatic daily batches</p>
            <p className="text-[10px] text-[#5C7599]">Toggles the background scheduler</p>
          </div>
          {loading || !status ? (
            <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
          ) : (
            <Switch
              checked={!!status.enabled}
              onCheckedChange={toggle}
              data-testid="followups-enabled-toggle"
            />
          )}
        </div>

        <Button
          onClick={runNow}
          disabled={running}
          className="bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/25 w-full sm:w-auto"
          data-testid="followups-run-now-btn"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PlayCircle className="w-4 h-4 mr-1" />}
          Run follow-ups now
        </Button>

        {status?.today && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs" data-testid="followups-batch-progress">
            <div className="rounded-md bg-[#000B18]/60 border border-white/5 p-2">
              <p className="text-[10px] uppercase text-[#5C7599]">Sent today</p>
              <p className="text-white font-mono" data-testid="followups-today-sent">{status.today.sent} / {status.today.cap}</p>
            </div>
            {[['member_profile', 'Members'], ['merchant_profile', 'Merchant profile'], ['merchant_services', 'Merchant services']].map(([k, label]) => (
              <div key={k} className="rounded-md bg-[#000B18]/60 border border-white/5 p-2">
                <p className="text-[10px] uppercase text-[#5C7599]">{label} waiting</p>
                <p className="text-[#FFEA00] font-mono" data-testid={`followups-pending-${k}`}>{status.pending?.[k] ?? 0}</p>
              </div>
            ))}
          </div>
        )}

        {last && (
          <div className="rounded-md bg-[#000B18]/60 border border-white/5 p-3 space-y-2 text-xs" data-testid="followups-last-run">
            <p className="text-white font-medium">Last run</p>
            <p className="text-[#A0B4CC]">Triggered by: <span className="text-[#00E5FF]">{last.triggered_by}</span></p>
            <p className="text-[#A0B4CC]">Finished: <span className="text-[#00E5FF]">{new Date(last.finished_at).toLocaleString()}</span></p>
            <ul className="text-[#A0B4CC] space-y-1">
              <li>· Members reminded: <span className="text-white font-mono">{last.member_profile?.sent ?? 0}</span> / {last.member_profile?.queried ?? 0}</li>
              <li>· Merchants (profile) reminded: <span className="text-white font-mono">{last.merchant_profile?.sent ?? 0}</span> / {last.merchant_profile?.queried ?? 0}</li>
              <li>· Merchants (services) reminded: <span className="text-white font-mono">{last.merchant_services?.sent ?? 0}</span> / {last.merchant_services?.queried ?? 0}</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default OnboardingFollowupsCard;
